// "Deployment options" for StackSet Create/Edit — automatic deployment and the
// account removal behaviour, in the AWS console's wording. Automatic
// deployment applies to OU targets only, so with no OU selected the control is
// shown disabled with the reason.
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { CK, FONT_BODY, insetPanelSx, sectionHeadingSx } from '../portalStyles';

// The tenant root is the "whole organization" target (AWS: the org root).
export const ROOT_TARGET_LABEL = 'All accounts in the organization';

// Renders an operation's actor: automatic-deployment runs record the
// system actor plus Details.trigger ("onboarding" | "schedule").
export const AUTO_DEPLOY_ACTOR = 'system:auto-deployment';
export function formatOperationActor(op) {
  if (!op || op.requestedByEmail !== AUTO_DEPLOY_ACTOR) return op?.requestedByEmail || '';
  let trigger = '';
  try { trigger = JSON.parse(op.details || '{}')?.trigger || ''; } catch { trigger = ''; }
  return trigger ? `Automatic deployment (${trigger})` : 'Automatic deployment';
}

const helpSx ={ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.body, mt: 0.5 };
const labelSx = { '& .MuiFormControlLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body } };

export default function AutoDeployOptions({
  autoDeploy, onAutoDeployChange, removal, onRemovalChange, hasOUTargets,
}) {
  const active = hasOUTargets && autoDeploy === 'on';
  return (
    <Box sx={{ ...insetPanelSx }}>
      <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mb: 0.5 }}>Automatic deployment</Typography>
      <RadioGroup
        row
        value={hasOUTargets ? autoDeploy : 'off'}
        onChange={(e) => onAutoDeployChange(e.target.value)}
      >
        <FormControlLabel value="on" control={<Radio size="small" />} label="Activated" disabled={!hasOUTargets} sx={labelSx} />
        <FormControlLabel value="off" control={<Radio size="small" />} label="Deactivated" disabled={!hasOUTargets} sx={labelSx} />
      </RadioGroup>
      <Typography sx={helpSx}>
        {hasOUTargets
          ? 'Deploys this StackSet to accounts that are created in, onboarded to, or moved into the target OUs, in every selected region. Accounts you add individually are not affected.'
          : 'Applies to OU targets.'}
      </Typography>
      {active && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ ...sectionHeadingSx, fontSize: '1rem', mb: 0.5 }}>Account removal behavior</Typography>
          <RadioGroup row value={removal} onChange={(e) => onRemovalChange(e.target.value)}>
            <FormControlLabel value="delete" control={<Radio size="small" />} label="Delete stacks" sx={labelSx} />
            <FormControlLabel value="retain" control={<Radio size="small" />} label="Retain stacks" sx={labelSx} />
          </RadioGroup>
          <Typography sx={helpSx}>
            What happens to an account&apos;s stacks when it leaves every target OU. Large removals are
            held for review instead of applied automatically.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
